import { useEffect, useRef, useState } from "react"

import { fetchJobsList, fetchJobStatus, type CampaignJob } from "@/api/hooks/campaigns"

/**
 * 3-second polling loop for the job queue panel. Parity:
 * `jobs.js:70-113`.
 *
 *   - On mount: GET /api/campaigns/jobs once to seed the panel and
 *     auto-resume polling for any active jobs.
 *   - While active: poll EACH tracked job via
 *     GET /api/campaigns/job-status/{id} every 3 seconds. This is the
 *     T3.4 architectural change from the legacy full-list polling —
 *     per-id calls are cheaper and don't flush the list for jobs we
 *     don't care about.
 *   - Stop condition: every tracked job reports `completed` or
 *     `failed`. Interval is cleared on unmount too.
 *   - `trackJobs(ids)` is the imperative hook callers invoke after
 *     submitting a batch of campaigns.
 */

interface State {
  jobs: CampaignJob[]
  panelVisible: boolean
}

export function useJobPolling() {
  const [state, setState] = useState<State>({ jobs: [], panelVisible: false })
  const trackedIds = useRef<Set<string>>(new Set())
  const timer = useRef<number | null>(null)
  const mounted = useRef(true)

  const applyJobsFromList = (jobs: CampaignJob[]) => {
    if (!mounted.current) return
    setState({ jobs, panelVisible: true })
  }

  const mergeSingleJob = (updated: CampaignJob) => {
    if (!mounted.current) return
    setState((prev) => {
      const exists = prev.jobs.some((j) => j.job_id === updated.job_id)
      const nextJobs = exists
        ? prev.jobs.map((j) => (j.job_id === updated.job_id ? updated : j))
        : [updated, ...prev.jobs]
      return { jobs: nextJobs, panelVisible: true }
    })
  }

  const stopTimer = () => {
    if (timer.current !== null) {
      window.clearInterval(timer.current)
      timer.current = null
    }
  }

  const startTimer = () => {
    if (timer.current !== null) return
    timer.current = window.setInterval(async () => {
      if (trackedIds.current.size === 0) {
        stopTimer()
        return
      }
      for (const id of Array.from(trackedIds.current)) {
        try {
          const job = await fetchJobStatus(id)
          mergeSingleJob(job)
          if (job.status === "completed" || job.status === "failed") {
            trackedIds.current.delete(id)
          }
        } catch {
          // Silent — keep polling; backend likely has a transient blip.
        }
      }
      if (trackedIds.current.size === 0) stopTimer()
    }, 3000)
  }

  // Initial mount: list existing jobs + auto-resume polling for
  // active ones.
  useEffect(() => {
    mounted.current = true
    let cancelled = false
    ;(async () => {
      try {
        const jobs = await fetchJobsList()
        if (cancelled) return
        if (jobs.length > 0) {
          applyJobsFromList(jobs)
          const active = jobs.filter(
            (j) => j.status === "pending" || j.status === "processing",
          )
          if (active.length > 0) {
            active.forEach((j) => trackedIds.current.add(j.job_id))
            startTimer()
          }
        }
      } catch {
        // Ignore — user may not have any jobs yet.
      }
    })()

    return () => {
      cancelled = true
      mounted.current = false
      stopTimer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trackJobs = (ids: string[]) => {
    ids.forEach((id) => trackedIds.current.add(id))
    // Seed the panel immediately with any jobs we already have so the
    // caller sees an optimistic row right away.
    setState((prev) => ({ ...prev, panelVisible: true }))
    startTimer()
  }

  return {
    jobs: state.jobs,
    panelVisible: state.panelVisible,
    trackJobs,
  }
}
